import { useState, useEffect, useCallback } from 'react'
import { Box, Flex, Text, VStack, Input, Spinner } from '@minetrace/ui'
import { toaster, ConfirmDialog } from '@minetrace/ui'
import { LuArrowLeft, LuPlus, LuTrash2, LuChevronUp, LuChevronDown } from 'react-icons/lu'
import { useNavigate, useParams } from 'react-router-dom'
import { themeColors, brand } from '../../../theme'
import { consentTemplateService } from '../../../api/services'
import type { ConsentTemplateDetail, ConsentTemplateItemDto } from '../../../api/models/consentTemplate.models'

const colors = themeColors

const labelStyle = {
  fontSize: 'xs' as const,
  fontWeight: '700' as const,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  color: colors.textMuted,
  mb: 2,
}

function PrimaryButton({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <Box as="button" px={5} py={2.5} borderRadius="md" bg={disabled ? '#b5aa9d' : brand.primary}
      color="white" fontSize="sm" fontWeight="700"
      cursor={disabled ? 'not-allowed' : 'pointer'}
      _hover={disabled ? {} : { opacity: 0.9 }}
      onClick={disabled ? undefined : onClick}>
      {label}
    </Box>
  )
}

function GhostButton({ label, icon, onClick }: { label: string; icon?: React.ReactNode; onClick: () => void }) {
  return (
    <Box as="button" px={4} py={2.5} borderRadius="md" border="1px solid" borderColor={colors.border}
      color={colors.text} fontSize="sm" fontWeight="600" display="flex" alignItems="center" gap={2}
      cursor="pointer" _hover={{ bg: colors.bgHover }} onClick={onClick}>
      {icon}{label}
    </Box>
  )
}

export default function EditorConsentimientoPage() {
  const navigate = useNavigate()
  const { id } = useParams()

  const [loading, setLoading] = useState(true)
  const [template, setTemplate] = useState<ConsentTemplateDetail | null>(null)
  const [title, setTitle] = useState('')
  const [introduction, setIntroduction] = useState('')
  const [items, setItems] = useState<ConsentTemplateItemDto[]>([])
  const [saving, setSaving] = useState(false)
  const [publishOpen, setPublishOpen] = useState(false)
  const [publishing, setPublishing] = useState(false)

  const readOnly = template ? !template.isDraft : false

  const load = useCallback(async () => {
    if (!id) return
    setLoading(true)
    try {
      const data = await consentTemplateService.getById(id)
      setTemplate(data)
      setTitle(data.title)
      setIntroduction(data.introduction ?? '')
      setItems(data.items.map((i) => ({ ...i })))
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'No se pudo cargar la plantilla' })
      navigate('/app/maestros/consentimiento')
    } finally {
      setLoading(false)
    }
  }, [id, navigate])

  useEffect(() => { load() }, [load])

  const updateItem = (idx: number, patch: Partial<ConsentTemplateItemDto>) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)))
  }

  const moveItem = (idx: number, dir: -1 | 1) => {
    setItems((prev) => {
      const next = [...prev]
      const target = idx + dir
      if (target < 0 || target >= next.length) return prev
      ;[next[idx], next[target]] = [next[target]!, next[idx]!]
      return next.map((it, i) => ({ ...it, order: i }))
    })
  }

  const removeItem = (idx: number) => {
    setItems((prev) => prev.filter((_, i) => i !== idx).map((it, i) => ({ ...it, order: i })))
  }

  const addItem = () => {
    setItems((prev) => [...prev, { order: prev.length, heading: '', body: '', isRequired: true }])
  }

  const validate = (): string | null => {
    if (!title.trim()) return 'El título es requerido'
    if (items.length === 0) return 'Debe registrar al menos un ítem'
    if (items.some((i) => !i.body.trim())) return 'Cada ítem debe tener un contenido'
    return null
  }

  const handleSave = async (): Promise<boolean> => {
    if (!id) return false
    const err = validate()
    if (err) { toaster.error({ description: err }); return false }
    setSaving(true)
    try {
      const updated = await consentTemplateService.update(id, {
        title: title.trim(),
        introduction: introduction.trim() || null,
        items: items.map((it, i) => ({
          order: i,
          heading: it.heading?.trim() || null,
          body: it.body.trim(),
          isRequired: it.isRequired,
        })),
      })
      setTemplate(updated)
      toaster.success({ description: 'Borrador guardado' })
      return true
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'No se pudo guardar el borrador' })
      return false
    } finally {
      setSaving(false)
    }
  }

  const handlePublish = async () => {
    if (!id) return
    setPublishing(true)
    try {
      const saved = await handleSave()
      if (!saved) { setPublishing(false); setPublishOpen(false); return }
      await consentTemplateService.publish(id)
      toaster.success({ description: 'Versión publicada y activada' })
      navigate('/app/maestros/consentimiento')
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'No se pudo publicar la versión' })
    } finally {
      setPublishing(false)
      setPublishOpen(false)
    }
  }

  if (loading) {
    return <Flex justify="center" py={20}><Spinner size="lg" color={brand.primary} /></Flex>
  }

  return (
    <VStack gap={6} alignItems="stretch">
      <Flex justifyContent="space-between" alignItems="center">
        <Box>
          <Flex alignItems="center" gap={2} mb={1}>
            <Box as="button" onClick={() => navigate('/app/maestros/consentimiento')}
              cursor="pointer" color={colors.textMuted} _hover={{ color: colors.text }}>
              <LuArrowLeft size={20} />
            </Box>
            <Text fontSize="2xl" fontWeight="800" color={colors.text} letterSpacing="-0.02em">
              {readOnly ? `Consentimiento v${template?.version}` : 'Editar borrador de consentimiento'}
            </Text>
          </Flex>
          <Text fontSize="sm" color={colors.textMuted}>
            {readOnly
              ? `Versión publicada — solo lectura. Publicada por ${template?.publishedByUsername ?? '—'}.`
              : 'Edita el contenido y publica una nueva versión activa.'}
          </Text>
        </Box>
        {!readOnly && (
          <Flex gap={3}>
            <GhostButton label={saving ? 'Guardando...' : 'Guardar borrador'} onClick={() => { if (!saving) handleSave() }} />
            <PrimaryButton label="Publicar" onClick={() => setPublishOpen(true)} disabled={saving || publishing} />
          </Flex>
        )}
      </Flex>

      <Box bg={colors.bgCard} border="1px solid" borderColor={colors.border} borderRadius="xl" p={6}>
        <Box mb={5}>
          <Text {...labelStyle}>Título</Text>
          <Input value={title} onChange={(e) => setTitle(e.target.value.slice(0, 300))} disabled={readOnly}
            placeholder="Autorización de Tratamiento de Datos Personales — Ley N° 29733" maxLength={300} />
          <Text fontSize="xs" color={colors.textMuted} mt={1} textAlign="right" fontVariantNumeric="tabular-nums">
            {title.length}/300
          </Text>
        </Box>
        <Box>
          <Text {...labelStyle}>Introducción (opcional)</Text>
          <Box as="textarea" rows={4} value={introduction}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setIntroduction(e.target.value)}
            readOnly={readOnly}
            w="100%" p={3} borderRadius="md" bg={colors.bgCard} border="1px solid" borderColor={colors.border}
            fontSize="sm" color={colors.text} _focus={{ borderColor: brand.primary, outline: 'none' }} />
        </Box>
      </Box>

      <Flex justifyContent="space-between" alignItems="center">
        <Text fontSize="lg" fontWeight="700" color={colors.text}>Ítems del consentimiento</Text>
        {!readOnly && <GhostButton label="Agregar ítem" icon={<LuPlus size={16} />} onClick={addItem} />}
      </Flex>

      <VStack gap={4} alignItems="stretch">
        {items.length === 0 && (
          <Box p={6} textAlign="center" color={colors.textMuted} bg={colors.bgCard}
            border="1px dashed" borderColor={colors.border} borderRadius="xl">
            Sin ítems. {readOnly ? '' : 'Agrega al menos uno.'}
          </Box>
        )}
        {items.map((it, idx) => (
          <Box key={idx} bg={colors.bgCard} border="1px solid" borderColor={colors.border} borderRadius="xl" p={5}>
            <Flex justifyContent="space-between" alignItems="center" mb={3}>
              <Text fontSize="sm" fontWeight="700" color={colors.textMuted}>Ítem {idx + 1}</Text>
              {!readOnly && (
                <Flex gap={2}>
                  <Box as="button" onClick={() => moveItem(idx, -1)} cursor="pointer"
                    color={colors.textMuted} _hover={{ color: colors.text }}><LuChevronUp size={18} /></Box>
                  <Box as="button" onClick={() => moveItem(idx, 1)} cursor="pointer"
                    color={colors.textMuted} _hover={{ color: colors.text }}><LuChevronDown size={18} /></Box>
                  <Box as="button" onClick={() => removeItem(idx)} cursor="pointer"
                    color="#c8442f" _hover={{ opacity: 0.8 }}><LuTrash2 size={18} /></Box>
                </Flex>
              )}
            </Flex>
            <Box mb={3}>
              <Text {...labelStyle}>Encabezado (opcional)</Text>
              <Input value={it.heading ?? ''} disabled={readOnly}
                onChange={(e) => updateItem(idx, { heading: e.target.value.slice(0, 300) })}
                placeholder="1. Finalidad del Tratamiento" maxLength={300} />
              <Text fontSize="xs" color={colors.textMuted} mt={1} textAlign="right" fontVariantNumeric="tabular-nums">
                {(it.heading ?? '').length}/300
              </Text>
            </Box>
            <Box mb={3}>
              <Text {...labelStyle}>Contenido</Text>
              <Box as="textarea" rows={4} value={it.body}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => updateItem(idx, { body: e.target.value })}
                readOnly={readOnly}
                w="100%" p={3} borderRadius="md" bg={colors.bgCard} border="1px solid" borderColor={colors.border}
                fontSize="sm" color={colors.text} _focus={{ borderColor: brand.primary, outline: 'none' }} />
            </Box>
            <Flex alignItems="center" gap={2}>
              <Box as="input" type="checkbox" checked={it.isRequired}
                disabled={readOnly}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateItem(idx, { isRequired: e.target.checked })} />
              <Text fontSize="sm" color={colors.text}>Obligatorio</Text>
            </Flex>
          </Box>
        ))}
      </VStack>

      <ConfirmDialog
        open={publishOpen}
        onClose={() => { if (!publishing) setPublishOpen(false) }}
        onConfirm={handlePublish}
        title="Publicar nueva versión"
        confirmLabel="Publicar"
        cancelLabel="Cancelar"
        variant="warning"
        isLoading={publishing}
      >
        Se guardará el borrador y se publicará como la versión activa. La versión activa anterior pasará a histórica. Esta acción no se puede deshacer.
      </ConfirmDialog>
    </VStack>
  )
}
